import React, { Dispatch, SetStateAction, useEffect } from 'react'
import styled from '@emotion/styled'
import { Col, Divider, Modal, Row, Tabs, Tooltip } from 'antd'
import { GET_PRODUCTS_CATEGORIES } from 'components/ProductsAndModifiers/Products/ProductQueries'
import { useLazyQuery } from '@apollo/client'
import { ProductCategory } from 'components/ProductsAndModifiers/Products/Form'
import { InventoryItem, ModifierGroup, WithChanges } from './types'

interface InventoryChangesModalProps {
  visible: boolean
  loading: boolean
  onOk: () => void
  onCancel: Dispatch<SetStateAction<boolean>>
  variantsWithChanges: WithChanges | undefined
  modifiersWithChanges: WithChanges | undefined
  modifierGroupsData: ModifierGroup[] | undefined
}

type ChangesColumnProps = {
  changesCount: number
  changes?: InventoryItem[]
  title: string
  loading?: boolean
  actionType: 'publish' | 'unpublish'
} & (VariantProps | ModifierProps)

type VariantProps = {
  type: 'variant'
  categories: ProductCategory[]
}

type ModifierProps = {
  type: 'modifier'
  modifierGroups: ModifierGroup[] | undefined
}

const InventoryChangesModal = (props: InventoryChangesModalProps) => {
  const { TabPane } = Tabs
  const {
    onOk,
    onCancel,
    visible,
    loading,
    variantsWithChanges,
    modifiersWithChanges,
    modifierGroupsData
  } = props

  const [
    getPublishedProducsCategories,
    {
      data: publishedProductsCategoriesData,
      loading: isLoadingProductsCategories
    }
  ] = useLazyQuery<{ products_categories: ProductCategory[] }>(
    GET_PRODUCTS_CATEGORIES
  )

  const [
    getUnpublishedProducsCategories,
    {
      data: unpublishedProductsCategoriesData,
      loading: isLoadingUnpublishedProductsCategories
    }
  ] = useLazyQuery<{ products_categories: ProductCategory[] }>(
    GET_PRODUCTS_CATEGORIES
  )

  useEffect(() => {
    if ((variantsWithChanges?.publishedItems.length ?? 0) > 0) {
      const publishedProductIds = [
        ...new Set(
          variantsWithChanges?.publishedItems.map(
            (v: InventoryItem) => v.productId
          )
        )
      ].map((id) => ({
        product_id: { _eq: id }
      }))

      getPublishedProducsCategories({
        variables: {
          productIds: publishedProductIds
        }
      })
    }
  }, [variantsWithChanges?.publishedItems.length])

  useEffect(() => {
    if ((variantsWithChanges?.unpublishedItems.length ?? 0) > 0) {
      const unpublishedProductIds = [
        ...new Set(
          variantsWithChanges?.unpublishedItems.map(
            (v: InventoryItem) => v.productId
          )
        )
      ].map((id) => ({
        product_id: { _eq: id }
      }))

      getUnpublishedProducsCategories({
        variables: {
          productIds: unpublishedProductIds
        }
      })
    }
  }, [variantsWithChanges?.unpublishedItems.length])

  return (
    <InventoryChangesModalStyled
      open={visible}
      title='Confirm inventory changes'
      cancelButtonProps={{ type: 'ghost', className: '-secondary' }}
      okText='save'
      okButtonProps={{ type: 'default', loading: loading, disabled: loading }}
      onCancel={() => onCancel(false)}
      onOk={() => onOk()}
      centered
    >
      <Tabs defaultActiveKey='0'>
        <TabPane
          key='1'
          tab={`Added (${
            (variantsWithChanges?.publishedItems.length ?? 0) +
            (modifiersWithChanges?.publishedItems.length ?? 0)
          })`}
        >
          <Row gutter={8} justify='space-around' className='_mt-16 _pr-16'>
            <Col span={11}>
              <ChangesColumn
                changesCount={variantsWithChanges?.publishedItems.length ?? 0}
                changes={variantsWithChanges?.publishedItems}
                title='Products'
                type='variant'
                actionType='publish'
                categories={
                  publishedProductsCategoriesData?.products_categories ?? []
                }
                loading={isLoadingProductsCategories}
              />
            </Col>
            <Col>
              <Divider type='vertical' />
            </Col>
            <Col span={11}>
              <ChangesColumn
                changesCount={modifiersWithChanges?.publishedItems.length ?? 0}
                changes={modifiersWithChanges?.publishedItems}
                title='Modifiers'
                type='modifier'
                actionType='publish'
                modifierGroups={modifierGroupsData}
              />
            </Col>
          </Row>
        </TabPane>
        <TabPane
          key='2'
          tab={`Removed (${
            (variantsWithChanges?.unpublishedItems.length ?? 0) +
            (modifiersWithChanges?.unpublishedItems.length ?? 0)
          })`}
        >
          <Row gutter={8} justify='space-around' className='_mt-16 _pr-16'>
            <Col span={11}>
              <ChangesColumn
                changesCount={variantsWithChanges?.unpublishedItems.length ?? 0}
                changes={variantsWithChanges?.unpublishedItems}
                title='Products'
                type='variant'
                actionType='unpublish'
                categories={
                  unpublishedProductsCategoriesData?.products_categories ?? []
                }
                loading={isLoadingUnpublishedProductsCategories}
              />
            </Col>
            <Col>
              <Divider type='vertical' />
            </Col>
            <Col span={11}>
              <ChangesColumn
                changesCount={
                  modifiersWithChanges?.unpublishedItems.length ?? 0
                }
                changes={modifiersWithChanges?.unpublishedItems}
                title='Modifiers'
                type='modifier'
                actionType='unpublish'
                modifierGroups={modifierGroupsData}
              />
            </Col>
          </Row>
        </TabPane>
      </Tabs>
    </InventoryChangesModalStyled>
  )
}

export const ChangesColumn = (props: ChangesColumnProps) => {
  const { changes, changesCount, title, type, loading, actionType } = props

  const filterInvolvedCategories = () => {
    if (type !== 'variant') return

    return changes?.map((c) => {
      if (!props.categories) return

      const involvedCategoryNames = props.categories
        .filter((g: ProductCategory) => c.productId === g.product_id)
        .map((pc) => pc.category.name)
      const isMoreThanOne = involvedCategoryNames?.length > 1

      return (
        <Tooltip
          title={
            <>
              <div className='_mb-8'>
                <strong>{c.label}</strong> will be{' '}
                {actionType === 'publish'
                  ? `added to ${
                      isMoreThanOne
                        ? 'these product categories'
                        : 'this product category'
                    }:`
                  : `removed from ${
                      isMoreThanOne
                        ? 'these product categories'
                        : 'this product category'
                    }:`}
              </div>
              <ul className='_pl-16'>
                {involvedCategoryNames.map((c) => (
                  <>
                    <li>{c}</li>
                  </>
                ))}
              </ul>
            </>
          }
          placement='left'
        >
          <Row className='list-item _mb-8' key={c.id} justify='center'>
            <span>{c.label}</span>
          </Row>
        </Tooltip>
      )
    })
  }

  const filterInvolvedModifierGroups = () => {
    if (type !== 'modifier') return

    return changes?.map((c) => {
      if (!props.modifierGroups) return

      const involvedModifierGroupNames: {
        name: string
        description: string
      }[] = props.modifierGroups
        .filter((g: ModifierGroup) => c.allGroupIds?.some((c) => c === g.id))
        .map((mg) => ({
          name: mg.name,
          description: mg.description
        }))
      const isMoreThanOne = involvedModifierGroupNames?.length > 1

      return (
        <Tooltip
          title={
            <>
              <div className='_mb-8'>
                <strong>{c.label}</strong> will be{' '}
                {actionType === 'publish'
                  ? `added to ${
                      isMoreThanOne
                        ? 'these modifier groups'
                        : 'this modifier group'
                    }:`
                  : `removed from ${
                      isMoreThanOne
                        ? 'these modifier groups'
                        : 'this modifier group'
                    }:`}
              </div>
              <ul className='_pl-16'>
                {involvedModifierGroupNames.map((c) => (
                  <>
                    <li>
                      {c.name} {c.description ? `(${c.description})` : ''}
                    </li>
                  </>
                ))}
              </ul>
            </>
          }
          placement='right'
        >
          <Row className='list-item _mb-8' key={c.id} justify='center'>
            <span>{c.label}</span>
          </Row>
        </Tooltip>
      )
    })
  }

  if (loading) return <></>

  return (
    <>
      <Row
        justify='center'
        className={`_mb-8 -title ${changesCount ? '' : '-transparent'}`}
      >
        {title} ({changesCount ?? 0})
      </Row>
      {type === 'variant' ? filterInvolvedCategories() : <></>}
      {type === 'modifier' ? filterInvolvedModifierGroups() : <></>}
    </>
  )
}

const InventoryChangesModalStyled = styled(Modal)`
  .ant-row.ant-row-center.list-item {
    padding: 4px 8px;
    border-radius: 4px;
    background-color: #f3f2f4;
    width: 100%;
    font-size: 13px;
    line-height: 1.5;
    color: #4a4a68;
    transition: background 150ms ease-in;

    span {
      max-width: 32ch;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    &:hover {
      background-color: #e7e5e9;
    }
  }

  .ant-modal-content {
    min-width: 720px;
    transform: translateX(-7.5vw);
  }

  .ant-tabs-content-holder {
    min-height: 200px;
    max-height: 400px;
    overflow-y: scroll;
    overflow-x: hidden;
  }

  .ant-row.-title {
    position: sticky;
    top: 0;
    left: 0;

    padding: 4px 8px;
    background-color: white;
    font-size: 14px;
    font-weight: 500;
  }

  .ant-row.-transparent {
    opacity: 0.4;
  }

  .ant-modal-body {
    padding-top: 16px;
  }
`

export default InventoryChangesModal
